import { FC, useRef } from 'react';
import Modal from 'components/shared/Modal';
import { Button } from '@kontentino/ui';
import { useMutation, useQueryClient } from 'react-query';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { ApiClientError } from 'api/client';
import { useTranslation } from 'react-i18next';
import { useToast } from 'app/hooks/useToast';
import AIGenerateButton from 'app/modules/aiContent/components/AIGenerateButton';
import { queryKey } from 'constants/queryKey';
import { BrandProfile } from 'app/modules/brandHub/types';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import useAiContentQueries from 'app/modules/aiContent/hooks/useAiContentQueries';
import BrandHubProfileForm, {
  BrandProfileFormValues,
} from '../BrandHubProfileForm';
import { UseFormReturn } from 'react-hook-form';
import Logger from 'utils/logger';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onBrandProfileSuggested: (brandProfile: BrandProfile) => void;
};

const BrandHubSuggestProfileModal: FC<Props> = ({
  isOpen,
  onClose,
  onBrandProfileSuggested,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { accountPlanType } = useSubscriptionInfo();
  const { openLimitModal } = useAiContentQueries({ accountPlanType });
  const queryClient = useQueryClient();
  const formRef = useRef<UseFormReturn<BrandProfileFormValues> | null>(null);

  const mutation = useMutation(BrandHubApi.suggestBrandProfile, {
    onSuccess: (brandProfile) => {
      const webCrawl = formRef.current?.getValues().webCrawl;
      if (!brandProfile.website && webCrawl?.url) {
        brandProfile.website = webCrawl.url;
      }
      onClose();
      onBrandProfileSuggested(brandProfile);
    },
    onError(e: ApiClientError) {
      if (e?.userMessage?.includes('AI generations')) {
        openLimitModal();
      } else {
        toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
      }
    },
    onSettled() {
      queryClient.invalidateQueries(queryKey.aiContentOptions());
    },
  });

  function onSubmit({ webCrawl, analyticsProfiles }: BrandProfileFormValues) {
    mutation.mutate({
      bodyText: webCrawl?.bodyText ?? undefined,
      planType: accountPlanType,
      logo: webCrawl?.logo ?? undefined,
      analyticsProfiles,
    });
  }

  return (
    <Modal
      dataName={{
        closeButton: 'brand-hub_suggest-profile_close',
        wrapper: 'brand-hub_suggest-profile_modal',
      }}
      dataCy={{
        closeButton: 'brand-hub_suggest-profile_close',
        wrapper: 'brand-hub_suggest-profile_modal',
      }}
      classNames={{
        root: '!tw-z-[1002]',
      }}
      open={isOpen}
      onClose={() => onClose()}
      isLoading={mutation.isLoading}
    >
      <Modal.Header
        title={t('generateBrandProfileTitle')}
        subtitle={t('generateBrandProfileFromWebsiteDescription')}
      />

      <Modal.Content className="tw-space-y-4 tw-pb-4">
        <BrandHubProfileForm
          onSubmit={onSubmit}
          isLoading={mutation.isLoading}
          formRef={(form) => {
            formRef.current = form;
          }}
        />
      </Modal.Content>
      <Modal.Footer withDivider>
        <Button
          data-name="brand-hub_suggest-profile_close"
          onClick={() => onClose()}
          variant="secondary"
        >
          {t('fillManually')}
        </Button>
        <AIGenerateButton
          type="button"
          data-name="brand-hub_suggest-profile_generate"
          onClick={() => {
            if (formRef.current) {
              formRef.current.handleSubmit(onSubmit, (errors) => {
                Logger.warn(
                  '[BrandHubSuggestProfileModal] form validation failed — Generate suppressed',
                  errors,
                );
              })();
            }
          }}
          isLoading={mutation.isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default BrandHubSuggestProfileModal;
